import {makeAutoObservable, onBecomeObserved, runInAction} from 'mobx';
import {makeLoggable} from 'mobx-log';

import config from '../config';
import apiService from '../transport/apiService';
import {OfferCoffeeCardStats, OffersItemWithMflId, OffersResponse} from '../transport/types';

import type {RootStore} from './rootStore';

export class OfferDetailStore {
  private rootStore: RootStore;
  offer: OffersItemWithMflId;

  linkedProducts: any[] | null = null;
  isLoadingLinkedProducts: boolean = false;
  isLoadingCoffeeCardStats: boolean = false;
  isApplyLoading: boolean = false;

  constructor(offer: OffersItemWithMflId, rootStore: RootStore) {
    this.offer = offer;
    this.rootStore = rootStore;

    makeAutoObservable(this);
    makeLoggable(this);

    /**
     * Fetch data lazily
     */
    onBecomeObserved(this, 'linkedProducts', this.load);
  }

  private load = async () => {
    await this.loadLinkedProducts();
  };

  private loadLinkedProducts = async () => {
    if (!this.linkedProducts && this.offer?.barcodes?.length > 0) {
      runInAction(() => {
        this.isLoadingLinkedProducts = true;
      });
      apiService
        .searchLines({
          product_codes: this.offer.barcodes?.flatMap(value =>
            value.split(';').map(barcode => barcode.trim()),
          ),
        })
        .then((lines: unknown) => {
          runInAction(() => {
            this.linkedProducts = lines as any;
            this.isLoadingLinkedProducts = false;
          });
        })
        .catch(e => {
          runInAction(() => {
            this.isLoadingLinkedProducts = false;
          });
          console.log(e);
        });
    }
  }

  /**
   * Apply the offer
   * todo. implemented as needed
   */
  async applyOffer() {
    runInAction(() => {
      this.isApplyLoading = true;
    });

    try {
      await apiService.applyOffer(this.offer.mflId);
      await this.rootStore.cartStore.loadOrderForUser();

      return {error: false};
    } catch (e) {
      console.log(e);
      return {error: 'Something went wrong'};
    } finally {
      runInAction(() => {
        this.isApplyLoading = false;
      });
    }
  }
}

export class OffersStore {
  private requestPromise: Promise<OffersResponse> | undefined;
  private rootStore: RootStore;

  isLoading: boolean = false;

  mflOfferData:
    | {id: string; external_metadata?: {externalref?: string}}[]
    | null = null;

  data: OffersItemWithMflId[] | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this);
    makeLoggable(this);

    /**
     * Fetch data lazily
     */
    onBecomeObserved(this, 'data', this.load);
  }

  private load = async () => {
    const isDevOrBetaEnvironment = ['DEV', 'BETA'].includes(config.ENV || '');
    const loadCoffeeCards = isDevOrBetaEnvironment;
    const coffeeCards: (OfferCoffeeCardStats & {contentId: string})[] = [];
    if (!this.requestPromise) {
      this.mflOfferData =
        ((await apiService.getStoreOffers()) as unknown as any[]) || [];

      const lmgOfferIds = this.mflOfferData.map(
        ({external_metadata}) => external_metadata?.externalref,
      );

      if (this.rootStore.shopSelectionStore.selectedStore && loadCoffeeCards) {
        try {
          const storeNumber = this.rootStore.shopSelectionStore.selectedStore?.store_number;
          const allCoffeeCards = await apiService.getCoffeeCards(storeNumber);
          const zenOffers = (await apiService.geZenOffers())

          const coffeeCardsWithLmgOfferId = zenOffers.map(offer => {
            const coffeeCard = allCoffeeCards.find(c => c.offer_id === +offer.id);
            if (coffeeCard) {
              return {...coffeeCard, contentId: offer.externalref};
            }
            return null;
          });
          coffeeCards.push(...coffeeCardsWithLmgOfferId.filter(x => x !== null));
          lmgOfferIds.push(...coffeeCardsWithLmgOfferId.filter(x => x !== null).map(x => x.contentId));
        } catch (e) {
          console.error(e);
        }
      }

      this.requestPromise = apiService.getOffers(
        lmgOfferIds.filter(id => id !== undefined),
      );
    }
    runInAction(() => {
      this.isLoading = true;
    });
    try {
      const allOfferContent = await this.requestPromise;
      const offers = allOfferContent.map(offerContent => {
        const offersItem = {
          ...offerContent,
          mflId:
            this.mflOfferData?.find(
              ({external_metadata}) =>
                external_metadata?.externalref === offerContent.offerId,
            )?.id || '',
        }

        if (offersItem.offerTrigger === 'Coffee Card') {
          const coffeeCard = coffeeCards.find(x => x.contentId === offerContent.offerId);
          if (coffeeCard) {
            if (offersItem.mflId === '' ) { // this means the offer is not coming through MFL API
              offersItem.mflId = coffeeCard.contentId;
            }
            offersItem.coffeeCardStats = coffeeCard;
          } else {
            console.error(`Coffee card stats not found for offer ${offerContent.offerId}`);
            return null;
          }
        }

        return offersItem;
      });
      runInAction(() => {
        this.data = offers.filter(offer => offer !== null);
      });
    } catch (err) {
      console.log(err);
    }

    runInAction(() => {
      this.isLoading = false;
    });
  };

  reset = () => {
    runInAction(() => {
      this.data = null;
      delete this.requestPromise;
    });
  };
}
